// 文件名: src/components/PlanDesigner.js
import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Upload, Ruler, Video, Edit, X, Camera, Trash2, RotateCw, Copy, Eye, EyeOff, ChevronDown, HelpCircle, FileUp, Download, Image as ImageIcon, UploadCloud, Spline, Type, Search } from 'lucide-react';
import { useTranslation } from '../i18n';
import pako from 'pako';
import html2canvas from 'html2canvas';
import 'react-image-crop/dist/ReactCrop.css';
import { difference } from 'polygon-clipping';
import {
    calculateTd1501Coverage,
    LENS_PRESETS as LENS_PRESETS_TD1501,
    LENS_PRESETS_TC as LENS_PRESETS_TC_TD1501,
    LENS_MODES as LENS_MODES_TD1501,
    LENS_MODES_TC as LENS_MODES_TC_TD1501,
    findOptimalAngle as findTd1501OptimalAngle,
    findMaxValidAngle as findTd1501MaxValidAngle
} from './TD1501';
import { calculateTd1502Coverage } from './TD1502';
import { calculateTd2000g2Bg2Coverage } from './Td2000g2Bg2';
import PersonHeightControl from './common/PersonHeightControl';
import { calculateTd2000g3Bg3Coverage } from './Td2000g3Bg3';
import td1501DefaultParams from '../config/td1501_params.json';
import td2000g2Bg2DefaultParams from '../config/td2000g2_bg2_params.json';
import td2000g3Bg3DefaultParams from '../config/td2000g3_bg3_params.json';
import { useUnit } from '../contexts/UnitContext';
import { formatLength, formatArea, getDisplayValue, getMetersFromInput, getUnitLabel } from '../utils/unitConversion';

const getFocalLengthDisplay = (lensType) => {
    if (!lensType) return null;
    if (lensType.includes('/')) {
        const parts = lensType.split('/');
        return parts.length > 1 ? parts[1] : lensType;
    }
    return lensType;
};

const CAMERA_MODELS = {
    'TD1501': { name: 'TD1501', hasTilt: true, defaultHeight: 3.0, defaultLens: '500W/3.6MM', tiltRangeXY: [-15, 15] },
    'TD1502': {
        name: 'TD1502',
        hasTilt: false,
        hasXYTilt: true,
        defaultXTilt: 0,
        defaultYTilt: 0,
        defaultHeight: 3.5,
        defaultLens: '2.2mm',
        lenses: {
            '2.2mm': { heightRange: [2.3, 5.5] },
            '3.6mm': { heightRange: [3.0, 9.0] },
            '6.0mm': { heightRange: [4.5, 15.0] },
            '12mm': { heightRange: [10.0, 30.0] },
            '16mm': { heightRange: [15.0, 40.0] }
        }
    },
    'TD2000G3/BG3': {
        name: 'TD2000G3/BG3',
        hasXYTilt: true,
        defaultHeight: 4.2,
        defaultXTilt: 0,
        defaultYTilt: 0,
        defaultLens: '2mm',
        lenses: {
            '2mm': { heightRange: [2.2, 8] },
            '8mm': { heightRange: [5, 18] }
        },
        tiltRangeXY: [-10, 10]
    },
    'TD2000G2/BG2': {
        name: 'TD2000G2/BG2',
        hasXYTilt: true,
        defaultHeight: 3.0,
        defaultXTilt: 0,
        defaultYTilt: 0,
        defaultLens: '2mm',
        lenses: {
            '2mm': { heightRange: [2.2, 8] },
            '8mm': { heightRange: [5, 18] }
        },
        tiltRangeXY: [-10, 10]
    },
};

const dummyT = (key, params) => {
    let str = key;
    if (params) {
        Object.keys(params).forEach(pKey => {
            str = str.replace(`\${${pKey}}`, params[pKey]);
        });
    }
    return str;
};

const getTd1501FullParams = (lensType, countingMethod = 'ReID') => {
    const isTC = countingMethod === 'Traffic Only';
    const presetsSource = isTC ? LENS_PRESETS_TC_TD1501 : LENS_PRESETS_TD1501;
    const lensParams = presetsSource[lensType];
    if (!lensParams) return { ...td1501DefaultParams };
    const baseParams = { ...td1501DefaultParams, ...lensParams };
    if (isTC) {
        baseParams.personHeight = 1.75;
        baseParams.minPixel = 20;
        baseParams.maxTiltAngle = 80;
        baseParams.personEntryCorrection = 0.4;
        baseParams.minCoverageDepth = 0.5;
        baseParams.recommendedPixel = 32;
    }
    return baseParams;
};

const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const coverageCalculators = {
    'TD1501': (inputs) => {
        const fullParams = getTd1501FullParams(inputs.lensType, inputs.countingMethod);
        return calculateTd1501Coverage(inputs, fullParams, dummyT);
    },
    'TD1502': (inputs) => calculateTd1502Coverage(inputs),
    'TD2000G2/BG2': (inputs) => calculateTd2000g2Bg2Coverage(inputs, td2000g2Bg2DefaultParams, dummyT),
    'TD2000G3/BG3': (inputs) => calculateTd2000g3Bg3Coverage(inputs, td2000g3Bg3DefaultParams, dummyT),
};

const calculatePolygonArea = (vertices) => {
    if (!vertices || vertices.length < 3) {
        return 0;
    }
    let area = 0;
    const n = vertices.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += vertices[i].x * vertices[j].y;
        area -= vertices[j].x * vertices[i].y;
    }
    return Math.abs(area / 2);
};

const BADGE_COLORS = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500',
    'bg-indigo-500', 'bg-teal-500', 'bg-orange-500'
];

const CameraEditor = ({ camera, onUpdate, onDelete, onClose, onDuplicate }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    const [localProps, setLocalProps] = useState(camera.properties);

    useEffect(() => {
        setLocalProps(camera.properties);
    }, [camera.properties]);

    const handleLocalInput = (prop, value) => {
        setLocalProps(prev => ({ ...prev, [prop]: value }));
    };

    const handleCommitChange = (prop, value) => {
        onUpdate(camera.id, prop, value);
    };

    const handleLensChange = (e) => {
        onUpdate(camera.id, 'lensType', e.target.value);
    };

    const handleModeChange = (e) => {
        onUpdate(camera.id, 'mode', e.target.value);
    };

    const modelInfo = CAMERA_MODELS[camera.model];

    const isTC = camera.properties.countingMethod === 'Traffic Only';

    const lensOptions = useMemo(() => {
        if (camera.model === 'TD1501') {
            return Object.keys(isTC ? LENS_PRESETS_TC_TD1501 : LENS_PRESETS_TD1501);
        }
        if (modelInfo?.lenses) {
            return Object.keys(modelInfo.lenses);
        }
        return [];
    }, [camera.model, modelInfo, isTC]);

    const td1501ModeOptions = useMemo(() => {
        if (camera.model !== 'TD1501' || !camera.properties.lensType) return null;
        const modesSource = isTC ? LENS_MODES_TC_TD1501 : LENS_MODES_TD1501;
        return modesSource[camera.properties.lensType] || null;
    }, [camera.model, camera.properties.lensType, isTC]);

    const heightBounds = useMemo(() => {
        if (!modelInfo) return [2, 18];
        if (camera.model === 'TD1501') {
            const { lensType, mode } = camera.properties;
            const lensModes = (isTC ? LENS_MODES_TC_TD1501 : LENS_MODES_TD1501)[lensType];
            if (lensModes && lensModes[mode]) {
                return lensModes[mode].heightRange;
            }
            return [2, 7]; // Fallback
        }
        if (modelInfo.lenses && modelInfo.lenses[camera.properties.lensType]?.heightRange) {
            return modelInfo.lenses[camera.properties.lensType].heightRange;
        }
        return modelInfo.heightRange || [2, 18];
    }, [camera.properties, camera.model, modelInfo, isTC]);

    const tiltRangeXY = modelInfo?.tiltRangeXY || [-45, 45];

    const td1501TiltBounds = useMemo(() => {
        if (camera.model !== 'TD1501') return null;
        const fullParams = getTd1501FullParams(camera.properties.lensType, camera.properties.countingMethod);
        const calcInputs = {
            installationHeight: camera.properties.height,
            personHeight: camera.properties.personHeight,
            mode: camera.properties.mode,
            countingMethod: camera.properties.countingMethod
        };
        const optimalAngle = findTd1501OptimalAngle(calcInputs, fullParams, dummyT);
        const maxValidAngle = findTd1501MaxValidAngle(calcInputs, fullParams, dummyT, optimalAngle);
        return { min: optimalAngle, max: maxValidAngle };
    }, [camera.properties.height, camera.properties.lensType, camera.model, camera.properties.personHeight, camera.properties.mode, camera.properties.countingMethod]);

    return (
        <div
            className="w-72 bg-white shadow-2xl p-4 flex flex-col rounded-lg select-none"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex justify-between items-center mb-4 cursor-move">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Edit className="w-5 h-5 text-cyan-600" /> Edit Camera</h3>
                <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-grow overflow-y-auto pr-2">
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-500">Model</label>
                    <p className="text-lg font-semibold text-gray-900">
                        {camera.model}
                        {lensOptions.length === 0 && camera.properties.lensType &&
                            <span className="ml-2 text-base font-normal text-gray-500">({camera.properties.lensType})</span>
                        }
                    </p>
                </div>
                {lensOptions.length > 0 && (
                    <div className="mb-4">
                        <label htmlFor="lensTypeSelect" className="block text-sm font-medium text-gray-600 mb-1">{t('lensModel')}</label>
                        <select id="lensTypeSelect" value={localProps.lensType} onChange={handleLensChange} className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500">
                            {lensOptions.map(lens => <option key={lens} value={lens}>{lens}</option>)}
                        </select>
                        {camera.model === 'TD2000G3/BG3' && localProps.lensType === '8mm' && (
                            <p className="text-red-600 text-sm mt-1">
                                TD2000BG3 does not currently support the 8mm lens.
                            </p>
                        )}
                    </div>
                )}
                {camera.model === 'TD1501' && (
                    <div className="mb-4">
                        <label htmlFor="countingMethodSelect" className="block text-sm font-medium text-gray-600 mb-1">{t('countingMethod')}</label>
                        <select id="countingMethodSelect" value={localProps.countingMethod || 'ReID'} onChange={(e) => onUpdate(camera.id, 'countingMethod', e.target.value)}
                            className={`w-full p-2 border rounded-md shadow-sm font-medium ${isTC ? 'border-amber-400 bg-amber-100 text-amber-800' : 'border-gray-300'}`}>
                            <option value="ReID">{t('countingMethodReID')}</option>
                            <option value="Traffic Only">{t('countingMethodTrafficOnly')}</option>
                        </select>
                        {isTC && <p className="text-xs text-amber-700 mt-1 font-medium">{t('trafficOnlyBadge')}</p>}
                    </div>
                )}
                {camera.model === 'TD1501' && td1501ModeOptions && (
                    <div className="mb-4">
                        <label htmlFor="modeSelect" className="block text-sm font-medium text-gray-600 mb-1">Mode</label>
                        <select id="modeSelect" value={localProps.mode} onChange={handleModeChange} className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500">
                            {Object.keys(td1501ModeOptions).map(modeKey => (
                                <option key={modeKey} value={modeKey}>{modeKey}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="space-y-4 mt-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">{t('installationHeight')}: <span className="font-bold text-cyan-600">{formatLength(localProps.height || 0, unitSystem, 1)}</span></label>
                        <span className="text-xs text-gray-500">({t('range')}: {formatLength(heightBounds[0], unitSystem, 1)} - {formatLength(heightBounds[1], unitSystem, 1)})</span>
                        <input type="range" min={getDisplayValue(heightBounds[0], unitSystem)} max={getDisplayValue(heightBounds[1], unitSystem)} step={unitSystem === 'imperial' ? "0.5" : "0.1"} value={getDisplayValue(localProps.height || 0, unitSystem)}
                            onInput={(e) => handleLocalInput('height', getMetersFromInput(e.target.value, unitSystem))}
                            onChange={(e) => handleCommitChange('height', getMetersFromInput(e.target.value, unitSystem))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                        {camera.model === 'TD2000G3/BG3' && localProps.lensType === '2mm' && localProps.height > 6 && (
                            <p className="text-red-600 text-sm mt-1">
                                TD2000BG3 does not support this height.
                            </p>
                        )}
                    </div>
                    {modelInfo.hasTilt && localProps.mode !== 'Top-Down' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">{t('tiltingAngle')}: <span className="font-bold text-cyan-600">{Number(localProps.tilt || 0).toFixed(0)} °</span></label>
                            {td1501TiltBounds && <span className="text-xs text-gray-500">({t('range')}: {td1501TiltBounds.min}° - {td1501TiltBounds.max}°)</span>}
                            <input type="range" min={td1501TiltBounds ? td1501TiltBounds.min : 0} max={td1501TiltBounds ? td1501TiltBounds.max : 90} step="1" value={localProps.tilt || 0}
                                onInput={(e) => handleLocalInput('tilt', e.target.value)}
                                onChange={(e) => handleCommitChange('tilt', e.target.value)}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                        </div>
                    )}
                    {(modelInfo.hasXYTilt || localProps.mode === 'Top-Down') && (<>
                        <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">{t('xTilt')}: <span className="font-bold text-cyan-600">{Number(localProps.xTilt || 0).toFixed(0)} °</span></label>
                            <span className="text-xs text-gray-500">({t('range')}: {tiltRangeXY[0]}° - {tiltRangeXY[1]}°)</span>
                            <input type="range" min={tiltRangeXY[0]} max={tiltRangeXY[1]} step="1" value={localProps.xTilt || 0}
                                onInput={(e) => handleLocalInput('xTilt', e.target.value)}
                                onChange={(e) => handleCommitChange('xTilt', e.target.value)}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">{t('yTilt')}: <span className="font-bold text-cyan-600">{Number(localProps.yTilt || 0).toFixed(0)} °</span></label>
                            <span className="text-xs text-gray-500">({t('range')}: {tiltRangeXY[0]}° - {tiltRangeXY[1]}°)</span>
                            <input type="range" min={tiltRangeXY[0]} max={tiltRangeXY[1]} step="1" value={localProps.yTilt || 0}
                                onInput={(e) => handleLocalInput('yTilt', e.target.value)}
                                onChange={(e) => handleCommitChange('yTilt', e.target.value)}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                        </div>
                    </>)}
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center gap-1"><RotateCw className="w-4 h-4" /> Rotation: <span className="font-bold text-cyan-600">{Number(localProps.rotation || 0).toFixed(0)} °</span></label>
                        <input type="range" min="-180" max="180" step="1" value={localProps.rotation || 0}
                            onInput={(e) => handleLocalInput('rotation', e.target.value)}
                            onChange={(e) => handleCommitChange('rotation', e.target.value)}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                    </div>
                    {camera.model === 'TD1501' && (
                        <div className="pt-4 border-t border-gray-100">
                            <PersonHeightControl
                                personHeight={localProps.personHeight}
                                onPersonHeightInput={(newHeight) => handleLocalInput('personHeight', newHeight)}
                                onPersonHeightChange={(newHeight) => handleCommitChange('personHeight', newHeight)}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                <button onClick={() => onDuplicate(camera.id)} className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-md transition-colors">
                    <Copy className="w-4 h-4" /> Duplicate Camera
                </button>
                <button onClick={(e) => onDelete(camera.id, e)} className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors">
                    <Trash2 className="w-4 h-4" /> Delete Camera
                </button>
            </div>
        </div>
    );
};

const SNAP_RADIUS = 10;

const PlanDesigner = ({ onBack, planState, setPlanState }) => {
    const { unitSystem } = useUnit();
    const uploadPlanInputRef = useRef(null);
    const importDesignInputRef = useRef(null);
    const imageContainerRef = useRef(null);
    const mainContainerRef = useRef(null);
    const statsContainerRef = useRef(null);
    const cameraListRef = useRef(null);
    const magnifierCanvasRef = useRef(null);

    const {
        floorPlan, floorPlanBase64, floorPlanNaturalSize, mode, scalePoints, scale, showScaleModal,
        scaleInput, cameras, showCameraModal, newCameraPosition, selectedCamera, isDragging,
        dragOffset, walls, selectedWallId, areWallsVisible, measurePoints, measurements, selectedMeasurementId,
        floorPlanOpacity, textBoxes, selectedTextBoxId, isDraggingTextBox, draggedWallPoint,
    } = planState;

    const [currentWallPoints, setCurrentWallPoints] = useState([]);
    const [showCameraList, setShowCameraList] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [isSnappingToStart, setIsSnappingToStart] = useState(false);
    const [isMagnifierEnabled, setIsMagnifierEnabled] = useState(false);
    const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0, cssX: 0, cssY: 0, visible: false });
    const [magnifierZoom, setMagnifierZoom] = useState(3);
    const [magnifierSize, setMagnifierSize] = useState(150);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [canvasZoom, setCanvasZoom] = useState(1); // 画布缩放比例 (0.25 - 3)
    const [isPanning, setIsPanning] = useState(false); // 是否正在拖拽画布
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 }); // 画布平移偏移
    const panStartRef = useRef({ x: 0, y: 0, initialPanX: 0, initialPanY: 0 }); // 拖拽起始位置

    const [editorCamera, setEditorCamera] = useState(null);
    const [editorPosition, setEditorPosition] = useState({ x: 0, y: 0 });
    const [isDraggingEditor, setIsDraggingEditor] = useState(false);
    const editorDragStartRef = useRef({ x: 0, y: 0, initialX: 0, initialY: 0 });

    const setState = useCallback((newState) => setPlanState(prev => ({ ...prev, ...newState })), [setPlanState]);

    // 将 CSS 像素坐标转换为底图的自然像素坐标（用于处理浏览器缩放）
    // 注意：必须使用容器（imageContainerRef）的边界矩形而非图片的，
    // 因为 SVG 是相对于容器定位的（absolute top-0 left-0）
    const cssToNaturalCoords = useCallback((cssX, cssY) => {
        if (!floorPlanNaturalSize || !imageContainerRef.current) {
            return { x: cssX, y: cssY };
        }
        const rect = imageContainerRef.current.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
            return { x: cssX, y: cssY };
        }
        const scaleX = floorPlanNaturalSize.width / rect.width;
        const scaleY = floorPlanNaturalSize.height / rect.height;
        return {
            x: cssX * scaleX,
            y: cssY * scaleY
        };
    }, [floorPlanNaturalSize]);

    const addMeasurementPoint = (point) => {
        const newPoints = [...measurePoints, point];
        if (newPoints.length === 2 && scale) {
            const [p1, p2] = newPoints;
            const pixelDistance = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
            const realDistance = pixelDistance / scale.ratio;
            const newMeasurement = {
                id: Date.now(),
                points: newPoints,
                distance: realDistance
            };
            setState({
                measurements: [...measurements, newMeasurement],
                measurePoints: [],
            });
        } else {
            setState({ measurePoints: newPoints });
        }
    };

    const snappedMousePosition = useMemo(() => {
        if (mode !== 'measuring' || measurePoints.length !== 1) {
            return mousePosition;
        }

        const p1 = measurePoints[0];
        const p2 = mousePosition;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;

        if (dx === 0 && dy === 0) {
            return mousePosition;
        }

        const angleRad = Math.atan2(dy, dx);
        const angleDeg = Math.abs(angleRad * 180 / Math.PI);
        const snapAngleThreshold = 10;

        if (angleDeg < snapAngleThreshold || angleDeg > (180 - snapAngleThreshold)) {
            return { x: p2.x, y: p1.y };
        }

        if (Math.abs(angleDeg - 90) < snapAngleThreshold) {
            return { x: p1.x, y: p2.y };
        }

        return mousePosition;
    }, [mode, measurePoints, mousePosition]);

    const handleOpenEditorClick = useCallback(() => {
        if (!selectedCamera || !imageContainerRef.current) return;

        const panelWidth = 288;
        const panelMargin = 24;
        const cameraIconRadius = 16;
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const camPos = selectedCamera.position;

        let left;
        if (camPos.x + cameraIconRadius + panelMargin + panelWidth > containerRect.width) {
            left = camPos.x - panelWidth - cameraIconRadius - panelMargin;
        } else {
            left = camPos.x + cameraIconRadius + panelMargin;
        }
        left = Math.max(10, Math.min(left, containerRect.width - panelWidth - 10));

        let top = camPos.y;
        let maxHeight = `${containerRect.height - top - 20}px`;
        let transform = 'none';

        if (top > containerRect.height / 2) {
            transform = 'translateY(-100%)';
            maxHeight = `${top - 20}px`;
        }

        setEditorPosition({ x: left, y: top, transform, maxHeight });
        setEditorCamera(selectedCamera);
        setState({ selectedCamera: null });

    }, [selectedCamera, setState]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!selectedCamera || editorCamera) return; // Only when Edit button visible, but editor panel is not

            const isClickOnCanvas = imageContainerRef.current && imageContainerRef.current.contains(event.target);
            const isClickOnCameraIcon = event.target.closest('.cursor-grab');
            const isClickOnEditButton = event.target.closest('[data-edit-button-id]');

            // If the click is inside the canvas, but not on a camera or the edit button, it's a "blank space" click.
            if (isClickOnCanvas && !isClickOnCameraIcon && !isClickOnEditButton) {
                setState(prev => ({ ...prev, selectedCamera: null }));
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [selectedCamera, editorCamera, setState]);

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                let restoredCameras = cameras;
                if (mode === 'drawing_wall') {
                    restoredCameras = cameras.map(cam => {
                        const { preDrawVisible, ...rest } = cam;
                        return { ...rest, isVisible: preDrawVisible ?? cam.isVisible };
                    });
                }

                setState({
                    mode: 'idle',
                    scalePoints: [],
                    measurePoints: [],
                    showScaleModal: false,
                    showCameraModal: false,
                    selectedCamera: null,
                    selectedWallId: null,
                    selectedMeasurementId: null,
                    selectedTextBoxId: null,
                    cameras: restoredCameras,
                    floorPlanOpacity: areWallsVisible ? (walls.length > 0 ? 0.5 : 1.0) : 1.0,
                });
                setEditorCamera(null);
                setCurrentWallPoints([]);
                setIsSnappingToStart(false);
                setShowCameraList(false);
            }
        };
        const handleClickOutside = (event) => {
            if (cameraListRef.current && !cameraListRef.current.contains(event.target)) {
                setShowCameraList(false);
            }
        };

        window.addEventListener('keydown', handleEsc);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setState, cameras, mode, walls.length, areWallsVisible]);

    useEffect(() => {
        if (!isMagnifierEnabled || !magnifierPosition.visible || !magnifierCanvasRef.current || !imageContainerRef.current) {
            return;
        }

        const magCanvas = magnifierCanvasRef.current;
        const magCtx = magCanvas.getContext('2d');
        const imgElement = imageContainerRef.current.querySelector('img');
        const svgElement = imageContainerRef.current.querySelector('svg');

        if (!imgElement || !svgElement) {
            return;
        }

        const zoomLevel = magnifierZoom;
        const magCanvasSize = magnifierSize;
        magCanvas.width = magCanvasSize;
        magCanvas.height = magCanvasSize;
        const lensSize = magCanvasSize / zoomLevel;

        const sourceX = magnifierPosition.x - lensSize / 2;
        const sourceY = magnifierPosition.y - lensSize / 2;

        magCtx.fillStyle = 'white';
        magCtx.fillRect(0, 0, magCanvasSize, magCanvasSize);

        magCtx.save();

        magCtx.scale(zoomLevel, zoomLevel);
        magCtx.translate(-sourceX, -sourceY);

        magCtx.drawImage(imgElement, 0, 0, imageContainerRef.current.clientWidth, imageContainerRef.current.clientHeight);

        const svgString = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        const svgImage = new Image();
        svgImage.onload = () => {
            try {
                magCtx.drawImage(svgImage, 0, 0);
            } catch (e) {
                console.error("Error drawing SVG on magnifier canvas", e);
            }
            URL.revokeObjectURL(url);
            magCtx.restore();

            const crosshairSize = 10;
            const center = magCanvasSize / 2;
            magCtx.strokeStyle = 'rgba(255, 0, 0, 0.7)';
            magCtx.lineWidth = 1;
            magCtx.beginPath();
            magCtx.moveTo(center - crosshairSize / 2, center);
            magCtx.lineTo(center + crosshairSize / 2, center);
            magCtx.moveTo(center, center - crosshairSize / 2);
            magCtx.lineTo(center, center + crosshairSize / 2);
            magCtx.stroke();
        };
        svgImage.onerror = () => {
            URL.revokeObjectURL(url);
            magCtx.restore();
            console.error("Failed to load SVG for magnifier.");
        };
        svgImage.src = url;

    }, [isMagnifierEnabled, magnifierPosition, magnifierZoom, magnifierSize]);


    const cameraCoverages = useMemo(() => {
        if (!scale || cameras.length === 0) return {};
        const coverages = {};
        cameras.forEach(cam => {
            if (cam.isNew || !cam.isVisible) return;
            const calculator = coverageCalculators[cam.model];
            if (calculator) {
                const inputs = {
                    installationHeight: cam.properties.height,
                    tiltingAngle: cam.properties.tilt,
                    xTilt: cam.properties.xTilt,
                    yTilt: cam.properties.yTilt,
                    lensType: cam.properties.lensType,
                    personHeight: cam.properties.personHeight || td1501DefaultParams.personHeight,
                    mode: cam.properties.mode,
                    countingMethod: cam.properties.countingMethod,
                };
                const result = calculator(inputs);
                if (result && !result.error) {
                    coverages[cam.id] = result;
                }
            }
        });
        return coverages;
    }, [cameras, scale]);

    const statistics = useMemo(() => {
        let colorIndex = 0;
        const getColor = () => BADGE_COLORS[colorIndex++ % BADGE_COLORS.length];

        const countBadges = Object.entries(cameras.reduce((acc, cam) => {
            const focalLength = getFocalLengthDisplay(cam.properties.lensType);
            const key = focalLength ? `${cam.model} (${focalLength})` : cam.model;
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {})).map(([model, count]) => ({
            model,
            count,
            color: getColor(),
        }));

        const totalArea = Object.entries(cameraCoverages).reduce((sum, [camId, coverage]) => {
            const cam = cameras.find(c => c.id.toString() === camId);
            if (!cam || !cam.isVisible) return sum;

            let area = 0;
            if (cam.model === 'TD1501' && coverage.summary?.isValid) {
                if (coverage.isTopDown) {
                    area = coverage.summary.area || 0;
                } else {
                    const { W1, W2, D1, D2 } = coverage.summary;
                    area = 0.5 * (W1 + W2) * (D2 - D1);
                }
            } else if (cam.model === 'TD1502' && coverage.area) {
                area = coverage.area;
            } else if ((cam.model.includes('G2') || cam.model.includes('G3')) && coverage.summary?.coverageField) {
                area = calculatePolygonArea(coverage.summary.coverageField);
            }
            return sum + area;
        }, 0);
        const areaBadge = {
            value: totalArea,
            color: getColor(),
        };

        let gpuText = 'N/A';
        if (cameras.length > 0) {
            const count = cameras.length;
            if (count >= 1 && count <= 4) gpuText = 'TD4001T2';
            else if (count >= 5 && count <= 8) gpuText = 'TD4001T6';
            else if (count >= 9 && count <= 16) gpuText = 'TD4001G2(T21)';
            else gpuText = 'Customized GPU';
        }
        const gpuBadge = {
            text: gpuText,
            color: getColor(),
        };

        return { countBadges, areaBadge, gpuBadge };
    }, [cameras, cameraCoverages]);

    const processUploadedFile = (file) => {
        if (file && file.type.startsWith('image/')) {
            const fileUrl = URL.createObjectURL(file);
            const img = new Image();
            img.src = fileUrl;
            img.onload = () => {
                // 同时读取文件为 Base64，保存到 state 中用于后续导出
                const reader = new FileReader();
                reader.onloadend = () => {
                    setState({
                        floorPlan: fileUrl,
                        floorPlanBase64: reader.result,
                        floorPlanNaturalSize: { width: img.naturalWidth, height: img.naturalHeight },
                        scale: null, cameras: [], selectedCamera: null, mode: 'idle', scalePoints: [],
                        walls: [], selectedWallId: null,
                    });
                    setEditorCamera(null);
                };
                reader.readAsDataURL(file);
            };
            img.onerror = () => {
                alert("Failed to load image.");
            };
        }
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        processUploadedFile(file);
        event.target.value = null;
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);
        const file = e.dataTransfer.files[0];
        processUploadedFile(file);
    };

    const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
        const byteCharacters = atob(b64Data.split(',')[1]);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    };

    const handleExportDesign = async () => {
        if (!floorPlan) {
            alert("Please upload a floor plan before exporting.");
            return;
        }

        try {
            // 优先使用已保存的 Base64 数据，避免 fetch 可能失效的 Blob URL
            let base64Data = floorPlanBase64;
            if (!base64Data) {
                // 兜底方案：从页面上已加载的 <img> 元素通过 Canvas 提取
                const imgElement = imageContainerRef.current?.querySelector('img');
                if (imgElement) {
                    const canvas = document.createElement('canvas');
                    canvas.width = imgElement.naturalWidth;
                    canvas.height = imgElement.naturalHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(imgElement, 0, 0);
                    base64Data = canvas.toDataURL('image/png');
                } else {
                    throw new Error('No floor plan image available for export.');
                }
            }

            const designData = {
                floorPlan: base64Data,
                floorPlanNaturalSize: floorPlanNaturalSize,
                scale: scale,
                cameras: cameras,
                walls: walls,
                measurements: measurements,
                textBoxes: textBoxes,
            };

            const jsonString = JSON.stringify(designData);
            const compressed = pako.gzip(jsonString);

            const blob = new Blob([compressed], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            a.href = url;
            a.download = `design-${timestamp}.dat`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to export design:", error);
            alert("An error occurred while exporting the design.");
        }
    };

    const onImportFileSelected = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const compressed = new Uint8Array(e.target.result);
                const jsonString = pako.ungzip(compressed, { to: 'string' });
                const designData = JSON.parse(jsonString);

                if (designData.floorPlan && designData.scale && designData.cameras) {
                    const imageBlob = b64toBlob(designData.floorPlan);
                    const newFloorPlanUrl = URL.createObjectURL(imageBlob);

                    const importedTextBoxes = (designData.textBoxes || []).map(tb => ({
                        ...tb,
                        text: tb.text || 'Edit me',
                        theme: tb.theme || 'dark',
                        isEditing: false,
                    }));

                    // 如果设计文件包含 floorPlanNaturalSize，直接使用；否则从图片加载
                    if (designData.floorPlanNaturalSize) {
                        setPlanState(prev => ({
                            ...prev,
                            floorPlan: newFloorPlanUrl,
                            floorPlanBase64: designData.floorPlan, // 保存导入的 Base64 数据
                            floorPlanNaturalSize: designData.floorPlanNaturalSize,
                            scale: designData.scale,
                            cameras: designData.cameras,
                            walls: designData.walls || [],
                            measurements: designData.measurements || [],
                            textBoxes: importedTextBoxes,
                            mode: 'idle',
                            selectedCamera: null,
                            selectedWallId: null,
                            selectedTextBoxId: null,
                        }));
                        setEditorCamera(null);
                    } else {
                        // 旧格式设计文件，需要从图片加载原始尺寸
                        const img = new Image();
                        img.src = newFloorPlanUrl;
                        img.onload = () => {
                            setPlanState(prev => ({
                                ...prev,
                                floorPlan: newFloorPlanUrl,
                                floorPlanBase64: designData.floorPlan, // 保存导入的 Base64 数据
                                floorPlanNaturalSize: { width: img.naturalWidth, height: img.naturalHeight },
                                scale: designData.scale,
                                cameras: designData.cameras,
                                walls: designData.walls || [],
                                measurements: designData.measurements || [],
                                textBoxes: importedTextBoxes,
                                mode: 'idle',
                                selectedCamera: null,
                                selectedWallId: null,
                                selectedTextBoxId: null,
                            }));
                            setEditorCamera(null);
                        };
                        img.onerror = () => {
                            alert("Failed to load floor plan image from design file.");
                        };
                    }
                } else {
                    throw new Error("Invalid design file format.");
                }

            } catch (error) {
                console.error("Failed to import design:", error);
                alert("Failed to import design. The file may be corrupt or not a valid design file.");
            }
        };
        reader.onerror = () => {
            alert("Error reading file.");
        };
        reader.readAsArrayBuffer(file);

        event.target.value = null;
    };

    const handleExportToPicture = async () => {
        const imageContainer = imageContainerRef.current;
        const hasStats = cameras.length > 0;

        if (!imageContainer) {
            alert("Cannot export. Required elements are not ready.");
            return;
        }
        const imageElement = imageContainer.querySelector('img');
        const svgElement = imageContainer.querySelector('svg');

        if (!imageElement || !svgElement) {
            alert("Floor plan or SVG overlay is missing.");
            return;
        }

        let exportContainer;

        try {
            const dpr = window.devicePixelRatio || 1;
            const rect = imageElement.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) {
                alert("Cannot export, image has zero dimensions.");
                return;
            }

            exportContainer = document.createElement('div');
            exportContainer.style.position = 'absolute'; // Keep it off-screen
            exportContainer.style.left = '-9999px';
            exportContainer.style.width = `${rect.width}px`;

            const clonedSvg = svgElement.cloneNode(true);
            const statsHeight = hasStats ? 50 : 0;
            clonedSvg.setAttribute('height', rect.height + statsHeight);

            const finalImageContainer = document.createElement('div');
            finalImageContainer.style.width = `${rect.width}px`;
            finalImageContainer.style.height = `${rect.height + statsHeight}px`;

            const backgroundCanvas = document.createElement('canvas');
            backgroundCanvas.width = rect.width;
            backgroundCanvas.height = rect.height + statsHeight;
            const ctx = backgroundCanvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, backgroundCanvas.width, backgroundCanvas.height);
            ctx.drawImage(imageElement, 0, statsHeight, rect.width, rect.height);

            const svgTransformGroup = clonedSvg.querySelector('g[transform]');
            if (svgTransformGroup) {
                svgTransformGroup.setAttribute('transform', `translate(0, ${statsHeight})`);
            } else {
                const newG = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                newG.setAttribute('transform', `translate(0, ${statsHeight})`);
                while (clonedSvg.firstChild) {
                    newG.appendChild(clonedSvg.firstChild);
                }
                clonedSvg.appendChild(newG);
            }

            clonedSvg.querySelectorAll('foreignObject').forEach(fo => {
                const textboxDiv = fo.querySelector('div[data-textbox-id]');
                const editButton = fo.querySelector('div[data-edit-button-id]');
                if (textboxDiv || editButton) fo.style.display = 'none';
            });

            textBoxes.forEach(tb => {
                if (!tb || !tb.position) {
                    console.warn("Skipping textbox with missing position data:", tb);
                    return;
                }

                const tempSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                tempSvg.style.position = 'absolute'; tempSvg.style.visibility = 'hidden';
                document.body.appendChild(tempSvg);
                const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                tempText.setAttribute('font-size', `${tb.fontSize}px`);
                tempText.setAttribute('font-family', 'sans-serif');
                tempText.textContent = tb.text;
                tempSvg.appendChild(tempText);
                const bbox = tempText.getBBox();
                document.body.removeChild(tempSvg);

                const horizontalPadding = 16;
                const verticalPadding = 8;
                const rectWidth = bbox.width + horizontalPadding * 2;
                const rectHeight = bbox.height + verticalPadding * 2;
                const isDark = tb.theme === 'dark';
                const bgColor = isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)';
                const textColor = isDark ? '#FFFFFF' : '#000000';

                const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                rect.setAttribute('x', tb.position.x);
                rect.setAttribute('y', tb.position.y);
                rect.setAttribute('width', rectWidth);
                rect.setAttribute('height', rectHeight);
                rect.setAttribute('rx', 6);
                rect.setAttribute('ry', 6);
                rect.setAttribute('fill', bgColor);

                const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                text.setAttribute('x', tb.position.x + rectWidth / 2);
                text.setAttribute('y', tb.position.y + rectHeight / 2);
                text.setAttribute('fill', textColor);
                text.setAttribute('font-size', `${tb.fontSize}px`);
                text.setAttribute('font-family', 'sans-serif');
                text.setAttribute('text-anchor', 'middle');
                text.setAttribute('dominant-baseline', 'central');
                text.textContent = tb.text;

                const textGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                textGroup.setAttribute('transform', `translate(0, ${statsHeight})`);

                textGroup.appendChild(rect);
                textGroup.appendChild(text);

                clonedSvg.appendChild(textGroup);
            });

            if (hasStats) {
                const tempSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                tempSvg.style.position = 'absolute'; tempSvg.style.visibility = 'hidden';
                document.body.appendChild(tempSvg);

                const statItems = [];
                statItems.push({ text: "Statics:", isStrong: true });
                statistics.countBadges.forEach(badge => statItems.push({ text: `${badge.model}: ${badge.count}`, colorClass: badge.color }));
                statItems.push({ isSeparator: true });
                statItems.push({ text: "Total Coverage:", isStrong: true });
                statItems.push({ text: `${formatArea(statistics.areaBadge.value, unitSystem, 2)}`, colorClass: statistics.areaBadge.color });
                statItems.push({ isSeparator: true });
                statItems.push({ text: "GPU Recommendation:", isStrong: true });
                statItems.push({ text: statistics.gpuBadge.text, colorClass: statistics.gpuBadge.color });

                const statsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                const statsBackground = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                statsBackground.setAttribute('x', 0);
                statsBackground.setAttribute('y', 0);
                statsBackground.setAttribute('width', rect.width);
                statsBackground.setAttribute('height', statsHeight);
                statsBackground.setAttribute('fill', '#ffffff');
                statsBackground.setAttribute('stroke', '#e5e7eb');
                statsBackground.setAttribute('stroke-width', '1');
                statsGroup.appendChild(statsBackground);

                let currentX = 16;
                let currentY = statsHeight / 2;
                const itemGap = 8;
                const separatorWidth = 10;
                const verticalPadding = 4;

                statItems.forEach(item => {
                    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                    textElement.setAttribute('font-size', '12px');
                    textElement.setAttribute('font-family', 'sans-serif');
                    textElement.textContent = item.text;

                    if (item.isStrong) {
                        textElement.setAttribute('font-weight', 'bold');
                        textElement.setAttribute('fill', '#1f2937');
                    } else if (!item.isSeparator) {
                        textElement.setAttribute('fill', 'white');
                        textElement.setAttribute('font-weight', '600');
                    }
                    tempSvg.appendChild(textElement);
                    const bbox = textElement.getBBox();
                    tempSvg.removeChild(textElement);

                    let itemWidth;
                    if (item.isSeparator) {
                        itemWidth = separatorWidth;
                    } else {
                        itemWidth = bbox.width + (item.isStrong ? 0 : 16);
                    }

                    if (currentX + itemWidth > rect.width - 16) { }

                    if (item.isSeparator) {
                        const separator = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                        separator.setAttribute('x', currentX);
                        separator.setAttribute('y', currentY - 8);
                        separator.setAttribute('width', 1);
                        separator.setAttribute('height', 16);
                        separator.setAttribute('fill', '#d1d5db');
                        statsGroup.appendChild(separator);
                    } else if (item.isStrong) {
                        textElement.setAttribute('x', currentX);
                        textElement.setAttribute('y', currentY);
                        textElement.setAttribute('dominant-baseline', 'central');
                        statsGroup.appendChild(textElement);
                    } else {
                        const rectElement = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                        const itemHeight = bbox.height + verticalPadding * 2;
                        rectElement.setAttribute('x', currentX);
                        rectElement.setAttribute('y', currentY - itemHeight / 2);
                        rectElement.setAttribute('width', itemWidth);
                        rectElement.setAttribute('height', itemHeight);
                        rectElement.setAttribute('rx', 6);
                        rectElement.setAttribute('fill', window.getComputedStyle(document.querySelector(`.${item.colorClass}`)).backgroundColor);
                        statsGroup.appendChild(rectElement);
                        textElement.setAttribute('x', currentX + itemWidth / 2);
                        textElement.setAttribute('y', currentY);
                        textElement.setAttribute('text-anchor', 'middle');
                        textElement.setAttribute('dominant-baseline', 'central');
                        statsGroup.appendChild(textElement);
                    }

                    currentX += itemWidth + itemGap;
                });
                clonedSvg.insertBefore(statsGroup, clonedSvg.firstChild);
                document.body.removeChild(tempSvg);
            }

            finalImageContainer.appendChild(backgroundCanvas);
            finalImageContainer.appendChild(clonedSvg);
            exportContainer.appendChild(finalImageContainer);

            document.body.appendChild(exportContainer);
            const imageCanvas = await html2canvas(exportContainer, {
                useCORS: true,
                scale: dpr,
                backgroundColor: null,
            });
            document.body.removeChild(exportContainer);
            exportContainer = null;

            const dataUrl = imageCanvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `floorplan-design-${Date.now()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error) {
            console.error("Failed to export to picture:", error);
            alert("An error occurred while exporting the image.");
        } finally {
            if (exportContainer && document.body.contains(exportContainer)) {
                document.body.removeChild(exportContainer);
            }
        }
    };

    const handleDeleteMeasurement = (idToDelete, e) => {
        e.stopPropagation();
        setState({ measurements: measurements.filter(m => m.id !== idToDelete) });
    };

    const handleImageContainerClick = (e) => {
        if (!imageContainerRef.current || isDragging || isDraggingTextBox) return;
        // 必须使用容器的边界矩形，因为 SVG 是相对于容器定位的（absolute top-0 left-0）
        const rect = imageContainerRef.current.getBoundingClientRect();
        const cssX = e.clientX - rect.left;
        const cssY = e.clientY - rect.top;
        // 转换为底图自然坐标
        const { x, y } = cssToNaturalCoords(cssX, cssY);

        if (mode === 'scaling') {
            const newPoints = [...scalePoints, { x, y }];
            setState({ scalePoints: newPoints });
            if (newPoints.length === 2) { setState({ showScaleModal: true }); }
        } else if (mode === 'adding_camera') {
            setState({ newCameraPosition: { x, y }, showCameraModal: true, mode: 'idle' });
        } else if (mode === 'drawing_wall') {
            if (isSnappingToStart && currentWallPoints.length >= 2) {
                const finalWallPoints = [...currentWallPoints, currentWallPoints[0]];
                const newWalls = [...walls, { id: Date.now(), points: finalWallPoints }];
                const restoredCameras = cameras.map(cam => {
                    const { preDrawVisible, ...rest } = cam;
                    return { ...rest, isVisible: preDrawVisible ?? cam.isVisible };
                });
                setCurrentWallPoints([]);
                setIsSnappingToStart(false);
                setState({
                    mode: 'idle',
                    walls: newWalls,
                    cameras: restoredCameras,
                    floorPlanOpacity: areWallsVisible ? (newWalls.length > 0 ? 0.5 : 1.0) : 1.0,
                });
            } else {
                setCurrentWallPoints(prev => [...prev, { x, y }]);
            }
        } else if (mode === 'measuring') {
            let pointToAdd = { x, y };
            if (measurePoints.length === 1) {
                const p1 = measurePoints[0];
                const p2 = { x, y };
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                if (dx !== 0 || dy !== 0) {
                    const angleRad = Math.atan2(dy, dx);
                    const angleDeg = Math.abs(angleRad * 180 / Math.PI);
                    const snapAngleThreshold = 10;
                    if (angleDeg < snapAngleThreshold || angleDeg > (180 - snapAngleThreshold)) {
                        pointToAdd.y = p1.y;
                    } else if (Math.abs(angleDeg - 90) < snapAngleThreshold) {
                        pointToAdd.x = p1.x;
                    }
                }
            }
            addMeasurementPoint(pointToAdd);
        } else if (mode === 'adding_text') {
            const newTextBox = {
                id: Date.now(),
                text: 'Edit me',
                position: { x, y },
                fontSize: 14,
                theme: 'dark',
                isEditing: true,
            };
            setState({
                textBoxes: [...textBoxes, newTextBox],
                selectedTextBoxId: newTextBox.id,
                mode: 'idle',
            });
        } else {
            setState({ selectedCamera: null, selectedWallId: null, selectedMeasurementId: null, selectedTextBoxId: null });
        }
    };





    const handleSetMode = (newMode) => {
        setIsMagnifierEnabled(false);
        setState({
            mode: newMode,
            scalePoints: [],
            selectedCamera: null,
            selectedWallId: null,
            measurePoints: [],
            selectedMeasurementId: null,
            selectedTextBoxId: null,
        });
        if (newMode !== 'drawing_wall') {
            setCurrentWallPoints([]);
            setIsSnappingToStart(false);
        }
    };

    const handleToggleMeasureMode = () => {
        if (mode === 'measuring') {
            handleSetMode('idle');
        } else {
            handleSetMode('measuring');
        }
    };

    const handleToggleDrawWallMode = () => {
        if (mode === 'drawing_wall') {
            const newWalls = [...walls];
            if (currentWallPoints.length >= 2) {
                newWalls.push({ id: Date.now(), points: currentWallPoints });
            }

            const restoredCameras = cameras.map(cam => {
                const { preDrawVisible, ...rest } = cam;
                return { ...rest, isVisible: preDrawVisible ?? cam.isVisible };
            });

            setCurrentWallPoints([]);
            setIsSnappingToStart(false);
            setState({
                mode: 'idle',
                walls: newWalls,
                cameras: restoredCameras,
                floorPlanOpacity: areWallsVisible ? (newWalls.length > 0 ? 0.5 : 1.0) : 1.0,
            });
        } else {
            const hiddenCameras = cameras.map(cam => ({
                ...cam,
                preDrawVisible: cam.isVisible,
                isVisible: false,
            }));
            setIsSnappingToStart(false);
            setState({
                mode: 'drawing_wall',
                cameras: hiddenCameras,
                floorPlanOpacity: 0.3,
                scalePoints: [],
                measurePoints: [],
                selectedCamera: null,
                selectedWallId: null,
                selectedMeasurementId: null,
                selectedTextBoxId: null,
            });
        }
    };

    const handleImageContainerDoubleClick = (e) => {
        if (mode === 'drawing_wall') {
            e.stopPropagation();
            handleToggleDrawWallMode();
        }
    };

    const handleToggleWallVisibility = () => {
        const newVisibility = !areWallsVisible;
        setState({
            areWallsVisible: newVisibility,
            floorPlanOpacity: newVisibility ? (walls.length > 0 ? 0.5 : 1.0) : 1.0,
        });
    };

    const handleConfirmScale = () => {
        if (scalePoints.length !== 2 || !scaleInput || scaleInput <= 0) return;
        const [p1, p2] = scalePoints;
        const pixelDistance = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
        const meters = (unitSystem === 'imperial' ? scaleInput * 304.8 : scaleInput) / 1000;

        const newScale = { pixels: pixelDistance, meters: meters, ratio: pixelDistance / meters };

        const recalculatedMeasurements = measurements.map(m => {
            const [mp1, mp2] = m.points;
            const measurementPixelDistance = Math.sqrt(Math.pow(mp2.x - mp1.x, 2) + Math.pow(mp2.y - mp1.y, 2));
            const newRealDistance = measurementPixelDistance / newScale.ratio;
            return { ...m, distance: newRealDistance };
        });

        setState({
            scale: newScale,
            measurements: recalculatedMeasurements,
            showScaleModal: false,
            mode: 'idle',
            scalePoints: [],
        });
    };

    const handleSelectCameraModel = (modelKey) => {
        const model = CAMERA_MODELS[modelKey];

        const baseProperties = {
            height: model.defaultHeight || 0,
            xTilt: model.defaultXTilt,
            yTilt: model.defaultYTilt,
            lensType: model.defaultLens,
            rotation: 0,
            personHeight: modelKey === 'TD1501' ? td1501DefaultParams.personHeight : undefined,
            mode: modelKey === 'TD1501' ? 'Standard' : undefined,
            countingMethod: modelKey === 'TD1501' ? 'ReID' : undefined,
        };

        if (modelKey === 'TD1501') {
            const fullParams = getTd1501FullParams(baseProperties.lensType, baseProperties.countingMethod);
            const calcInputs = {
                installationHeight: baseProperties.height,
                personHeight: baseProperties.personHeight,
                mode: baseProperties.mode,
                countingMethod: baseProperties.countingMethod
            };
            const optimalAngle = findTd1501OptimalAngle(calcInputs, fullParams, dummyT);
            baseProperties.tilt = optimalAngle;
        } else {
            baseProperties.tilt = model.defaultTilt;
        }

        const newCamera = {
            id: Date.now(),
            model: modelKey,
            position: newCameraPosition,
            properties: baseProperties,
            name: '',
            isNew: true,
            isVisible: true,
            color: '#16a34a',
        };
        const newCameras = [...cameras, newCamera];
        setState({
            cameras: newCameras,
            showCameraModal: false,
            newCameraPosition: null,
            selectedCamera: newCamera,
        });
    };

    const updateCameraProperty = (camId, prop, value) => {
        const camToUpdate = cameras.find(c => c.id === camId);
        if (!camToUpdate) return;

        const parsedValue = typeof value === 'string' && !['lensType', 'mode', 'countingMethod'].includes(prop) ? parseFloat(value) : value;
        let updatedProperties = { ...camToUpdate.properties, [prop]: parsedValue };

        if (camToUpdate.model === 'TD1501') {
            const isFundamentalChange = ['lensType', 'mode', 'countingMethod'].includes(prop);
            const isInitialPersonHeightUpdate = camToUpdate.isNew && prop === 'personHeight';

            if (isFundamentalChange || isInitialPersonHeightUpdate) {
                let { lensType, mode, height, personHeight, countingMethod } = updatedProperties;
                const isTC = countingMethod === 'Traffic Only';
                const currentLensModes = isTC ? LENS_MODES_TC_TD1501 : LENS_MODES_TD1501;
                const currentLensPresets = isTC ? LENS_PRESETS_TC_TD1501 : LENS_PRESETS_TD1501;

                // Validate lens for current counting method
                if (!currentLensPresets[lensType]) {
                    lensType = Object.keys(currentLensPresets)[0];
                    updatedProperties.lensType = lensType;
                }

                const availableModesForLens = currentLensModes[lensType];
                if (!availableModesForLens || !availableModesForLens[mode]) {
                    mode = Object.keys(availableModesForLens || {})[0] || 'Standard';
                    updatedProperties.mode = mode;
                }

                // Update person height when switching counting method
                if (prop === 'countingMethod') {
                    personHeight = isTC ? 1.75 : td1501DefaultParams.personHeight;
                    updatedProperties.personHeight = personHeight;
                }

                const modeConfig = currentLensModes[lensType]?.[mode];
                if (modeConfig) {
                    const [minH, maxH] = modeConfig.heightRange;
                    height = Math.max(minH, Math.min(height, maxH));
                    updatedProperties.height = height;
                }

                if (prop !== 'tilt') {
                    const fullParams = getTd1501FullParams(lensType, countingMethod);
                    const calcInputs = { installationHeight: height, personHeight, mode, countingMethod };
                    const newOptimalAngle = findTd1501OptimalAngle(calcInputs, fullParams, dummyT);
                    updatedProperties.tilt = newOptimalAngle;
                }
            }
        }

        const updatedCameras = cameras.map(cam =>
            cam.id === camId
                ? { ...cam, properties: updatedProperties, isNew: false }
                : cam
        );
        const updatedEditorCamera = editorCamera && editorCamera.id === camId
            ? updatedCameras.find(c => c.id === camId)
            : editorCamera;

        setState({ cameras: updatedCameras });
        setEditorCamera(updatedEditorCamera);
    };

    const duplicateCamera = (camId) => {
        const camToDuplicate = cameras.find(c => c.id === camId);
        if (!camToDuplicate || !imageContainerRef.current) return;

        const rect = imageContainerRef.current.getBoundingClientRect();
        const offsetX = 20;
        const offsetY = 20;

        const newPosition = {
            x: Math.max(0, Math.min(camToDuplicate.position.x + offsetX, rect.width)),
            y: Math.max(0, Math.min(camToDuplicate.position.y + offsetY, rect.height)),
        };

        const newCamera = {
            ...camToDuplicate,
            id: Date.now(),
            position: newPosition,
            isNew: false,
            name: '',
            color: camToDuplicate.color || '#16a34a',
        };

        setState({
            cameras: [...cameras, newCamera],
            selectedCamera: newCamera,
        });
        setEditorCamera(null);
    };

    const deleteCamera = (camId, e) => {
        e.stopPropagation();
        setState({
            cameras: cameras.filter(cam => cam.id !== camId),
            selectedCamera: (selectedCamera && selectedCamera.id === camId) ? null : selectedCamera,
        });
        if (editorCamera && editorCamera.id === camId) {
            setEditorCamera(null);
        }
    };

    const handleCameraMouseDown = (e, cam) => {
        if (e.button !== 0) return;

        if (mode === 'measuring') {
            e.stopPropagation();
            addMeasurementPoint({ x: cam.position.x, y: cam.position.y });
            return;
        }

        e.stopPropagation();
        const rect = imageContainerRef.current.getBoundingClientRect();
        const cssX = e.clientX - rect.left;
        const cssY = e.clientY - rect.top;
        const { x: mouseX, y: mouseY } = cssToNaturalCoords(cssX, cssY);

        setState({
            isDragging: true, draggedCameraId: cam.id,
            dragOffset: { x: mouseX - cam.position.x, y: mouseY - cam.position.y },
            selectedCamera: cam,
            selectedWallId: null,
            selectedMeasurementId: null,
            selectedTextBoxId: null,
        });
    };

    const handleTextBoxMouseDown = (e, tb) => {
        if (mode === 'measuring' || e.button !== 0 || tb.isEditing) return;
        e.stopPropagation();
        const rect = imageContainerRef.current.getBoundingClientRect();
        const cssX = e.clientX - rect.left;
        const cssY = e.clientY - rect.top;
        const { x: mouseX, y: mouseY } = cssToNaturalCoords(cssX, cssY);

        setState({
            isDraggingTextBox: true,
            draggedTextBoxId: tb.id,
            dragOffset: { x: mouseX - tb.position.x, y: mouseY - tb.position.y },
            selectedTextBoxId: tb.id,
            selectedCamera: null,
            selectedWallId: null,
            selectedMeasurementId: null,
        });
    };

    const handleEditorDragStart = (e) => {
        e.stopPropagation();
        if (e.button !== 0) return;

        if (e.target.closest('input, select, button')) {
            return;
        }

        setIsDraggingEditor(true);
        editorDragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            initialX: editorPosition.x,
            initialY: editorPosition.y,
        };
    };

    const handleWallPointMouseDown = (e, wallId, pointIndex) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        const rect = imageContainerRef.current.getBoundingClientRect();
        const cssX = e.clientX - rect.left;
        const cssY = e.clientY - rect.top;
        const { x: mouseX, y: mouseY } = cssToNaturalCoords(cssX, cssY);

        // Find the wall and point to get initial offset if needed, 
        // but for points we usually just snap to mouse or keep offset. 
        // Let's use offset to prevent jumping if clicked slightly off-center.
        const wall = walls.find(w => w.id === wallId);
        const point = wall.points[pointIndex];

        setState({
            draggedWallPoint: { wallId, pointIndex },
            dragOffset: { x: mouseX - point.x, y: mouseY - point.y },
            selectedWallId: wallId, // Ensure it stays selected
        });
    };

    const handleMouseMove = (e) => {
        if (!imageContainerRef.current) return;
        const rect = imageContainerRef.current.getBoundingClientRect();
        const cssMouseX = e.clientX - rect.left;
        const cssMouseY = e.clientY - rect.top;
        // 转换为自然坐标
        const naturalCoords = cssToNaturalCoords(cssMouseX, cssMouseY);
        const mouseX = naturalCoords.x;
        const mouseY = naturalCoords.y;
        setMousePosition({ x: mouseX, y: mouseY });
        const isInside = cssMouseX >= 0 && cssMouseX <= rect.width && cssMouseY >= 0 && cssMouseY <= rect.height;

        if (isMagnifierEnabled) {
            // 放大镜 UI 定位使用 CSS 坐标，内容渲染使用自然坐标
            setMagnifierPosition({ x: mouseX, y: mouseY, cssX: cssMouseX, cssY: cssMouseY, visible: isInside });
        }

        if (isDraggingEditor) {
            const dx = e.clientX - editorDragStartRef.current.x;
            const dy = e.clientY - editorDragStartRef.current.y;
            setEditorPosition(prev => ({
                ...prev,
                x: editorDragStartRef.current.initialX + dx,
                y: editorDragStartRef.current.initialY + dy,
            }));
        } else if (isDragging) {
            const newX = mouseX - dragOffset.x;
            const newY = mouseY - dragOffset.y;
            // 使用自然坐标边界进行clamp
            const maxX = floorPlanNaturalSize ? floorPlanNaturalSize.width : rect.width;
            const maxY = floorPlanNaturalSize ? floorPlanNaturalSize.height : rect.height;
            const clampedX = Math.max(0, Math.min(newX, maxX));
            const clampedY = Math.max(0, Math.min(newY, maxY));

            setPlanState(prevState => {
                if (!prevState.isDragging) return prevState;
                const updatedCameras = prevState.cameras.map(cam =>
                    cam.id === prevState.draggedCameraId
                        ? { ...cam, position: { x: clampedX, y: clampedY } }
                        : cam
                );
                return { ...prevState, cameras: updatedCameras, selectedCamera: null };
            });
        } else if (isDraggingTextBox) {
            const newX = mouseX - dragOffset.x;
            const newY = mouseY - dragOffset.y;
            setPlanState(prevState => {
                if (!prevState.isDraggingTextBox) return prevState;
                const updatedTextBoxes = prevState.textBoxes.map(tb =>
                    tb.id === prevState.draggedTextBoxId
                        ? { ...tb, position: { x: newX, y: newY } }
                        : tb
                );
                return { ...prevState, textBoxes: updatedTextBoxes, selectedTextBoxId: null };
            });
        } else if (mode === 'drawing_wall' && currentWallPoints.length >= 2) {
            const startPoint = currentWallPoints[0];
            const dx = mouseX - startPoint.x;
            const dy = mouseY - startPoint.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            // SNAP_RADIUS 需要根据自然坐标调整
            const naturalSnapRadius = floorPlanNaturalSize
                ? SNAP_RADIUS * (floorPlanNaturalSize.width / rect.width)
                : SNAP_RADIUS;
            setIsSnappingToStart(distance < naturalSnapRadius);
        } else if (draggedWallPoint) {
            const newX = mouseX - dragOffset.x;
            const newY = mouseY - dragOffset.y;

            setPlanState(prevState => {
                const updatedWalls = prevState.walls.map(wall => {
                    if (wall.id === draggedWallPoint.wallId) {
                        const newPoints = [...wall.points];
                        newPoints[draggedWallPoint.pointIndex] = { x: newX, y: newY };

                        // If it's a closed loop (first and last points are same), update both if one is moved
                        // Actually, the current drawing logic adds the first point again at the end for closed loops?
                        // Let's check drawing logic. 
                        // Yes: const finalWallPoints = [...currentWallPoints, currentWallPoints[0]];
                        // So if we move index 0, we should move index length-1, and vice versa.
                        if (newPoints.length > 2 && (newPoints[0].x === newPoints[newPoints.length - 1].x && newPoints[0].y === newPoints[newPoints.length - 1].y)) {
                            if (draggedWallPoint.pointIndex === 0) {
                                newPoints[newPoints.length - 1] = { x: newX, y: newY };
                            } else if (draggedWallPoint.pointIndex === newPoints.length - 1) {
                                newPoints[0] = { x: newX, y: newY };
                            }
                        }

                        return { ...wall, points: newPoints };
                    }
                    return wall;
                });
                return { ...prevState, walls: updatedWalls };
            });
        } else if (isPanning) {
            // 画布拖拽平移
            const dx = e.clientX - panStartRef.current.x;
            const dy = e.clientY - panStartRef.current.y;
            setPanOffset({
                x: panStartRef.current.initialPanX + dx,
                y: panStartRef.current.initialPanY + dy,
            });
        }
    };

    const handleMouseUp = () => {
        if (isDragging) {
            setState({ isDragging: false, draggedCameraId: null });
        }
        if (isDraggingTextBox) {
            setState({ isDraggingTextBox: false, draggedTextBoxId: null });
        }
        if (isDraggingEditor) {
            setIsDraggingEditor(false);
        }
        if (draggedWallPoint) {
            setState({ draggedWallPoint: null });
        }
        if (isPanning) {
            setIsPanning(false);
        }
    };

    const toggleCameraVisibility = (camId) => {
        const updatedCameras = cameras.map(cam =>
            cam.id === camId ? { ...cam, isVisible: !cam.isVisible } : cam
        );
        setState({ cameras: updatedCameras });
    };

    const updateCameraColor = (camId, newColor) => {
        const updatedCameras = cameras.map(cam =>
            cam.id === camId ? { ...cam, color: newColor } : cam
        );
        setState({ cameras: updatedCameras });
    };

    const updateCameraName = (camId, newName) => {
        const updatedCameras = cameras.map(cam =>
            cam.id === camId ? { ...cam, name: newName } : cam
        );
        setState({ cameras: updatedCameras });
    };

    const handleTextBoxTextChange = (id, newText) => {
        const updatedTextBoxes = textBoxes.map(tb =>
            tb.id === id ? { ...tb, text: newText } : tb
        );
        setState({ textBoxes: updatedTextBoxes });
    };

    const handleTextBoxDoubleClick = (e, id) => {
        e.stopPropagation();
        if (mode === 'measuring') return;
        const updatedTextBoxes = textBoxes.map(tb =>
            tb.id === id ? { ...tb, isEditing: true } : { ...tb, isEditing: false }
        );
        setState({
            textBoxes: updatedTextBoxes,
            selectedTextBoxId: id,
            selectedCamera: null,
            selectedWallId: null,
            selectedMeasurementId: null,
        });
    };

    const handleTextBoxBlur = (id) => {
        const updatedTextBoxes = textBoxes.map(tb =>
            tb.id === id ? { ...tb, isEditing: false } : tb
        );
        setState({ textBoxes: updatedTextBoxes });
    };

    const handleDeleteTextBox = (id, e) => {
        e.stopPropagation();
        setState({
            textBoxes: textBoxes.filter(tb => tb.id !== id),
            selectedTextBoxId: null,
        });
    };

    const handleTextBoxThemeChange = (id, e) => {
        e.stopPropagation();
        const updatedTextBoxes = textBoxes.map(tb => {
            if (tb.id === id) {
                return { ...tb, theme: tb.theme === 'dark' ? 'light' : 'dark' };
            }
            return tb;
        });
        setState({ textBoxes: updatedTextBoxes });
    };

    const renderCoveragePolygon = (cam, coverageData) => {
        if (cam.isNew || !cam.isVisible) return null;

        const toMultiPolygon = (points) => [[points.map(p => [p.x, p.y])]];

        const { ratio } = scale;
        let points = [];
        if (cam.model === 'TD1501' && coverageData.summary) {
            if (!coverageData.summary.isValid) return null;
            if (coverageData.isTopDown && coverageData.details?.coverageField?.polygon) {
                // Use the true irregular 3D projected polygon
                points = coverageData.details.coverageField.polygon.map(p => ({ x: p.x, y: -p.y }));
            } else {
                const { D1, W1, D2, W2 } = coverageData.summary;
                points = [{ x: -W1 / 2, y: -D1 }, { x: W1 / 2, y: -D1 }, { x: W2 / 2, y: -D2 }, { x: -W2 / 2, y: -D2 }];
            }
        } else if ((cam.model.includes('G2') || cam.model.includes('G3')) && coverageData.summary?.coverageField) {
            points = coverageData.summary.coverageField.map(p => ({ x: p.x, y: -p.y }));
        } else if (cam.model === 'TD1502') {
            if (coverageData.details?.coverageField?.polygon) {
                // Use the true irregular 3D projected polygon
                points = coverageData.details.coverageField.polygon.map(p => ({ x: p.x, y: -p.y }));
            } else if (coverageData.viewWidth) {
                // Legacy fallback if details are missing
                const { viewWidth, viewDepth } = coverageData;
                const halfWidth = viewWidth / 2;
                const halfDepth = viewDepth / 2;
                points = [
                    { x: -halfWidth, y: -halfDepth }, { x: halfWidth, y: -halfDepth },
                    { x: halfWidth, y: halfDepth }, { x: -halfWidth, y: halfDepth },
                ];
            }
        }
        if (points.length === 0) return null;

        const scaledPoints = points.map(p => ({ x: p.x * ratio, y: p.y * ratio }));
        const angleRad = (cam.properties.rotation || 0) * Math.PI / 180;
        const cosA = Math.cos(angleRad);
        const sinA = Math.sin(angleRad);
        const rotatedPoints = scaledPoints.map(p => ({
            x: p.x * cosA - p.y * sinA,
            y: p.x * sinA + p.y * cosA,
        }));
        const transformedPoints = rotatedPoints.map(p => ({ x: p.x + cam.position.x, y: p.y + cam.position.y }));

        let finalPolygons = toMultiPolygon(transformedPoints);

        if (walls.length > 0 && areWallsVisible) {
            const allWallSegments = walls.flatMap(wall => {
                const segments = [];
                for (let i = 0; i < wall.points.length - 1; i++) {
                    segments.push([wall.points[i], wall.points[i + 1]]);
                }
                return segments;
            });

            const camPos = cam.position;
            const FAR = 10000;

            for (const segment of allWallSegments) {
                const [p1, p2] = segment;

                const v1 = { x: p1.x - camPos.x, y: p1.y - camPos.y };
                const v2 = { x: p2.x - camPos.x, y: p2.y - camPos.y };

                const crossProduct = v1.x * v2.y - v1.y * v2.x;
                if (Math.abs(crossProduct) < 1e-9) {
                    continue;
                }

                const l1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
                const l2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

                if (l1 === 0 || l2 === 0) continue;

                const p3 = { x: camPos.x + v1.x / l1 * FAR, y: camPos.y + v1.y / l1 * FAR };
                const p4 = { x: camPos.x + v2.x / l2 * FAR, y: camPos.y + v2.y / l2 * FAR };

                const shadowPolygon = toMultiPolygon([p1, p2, p4, p3]);

                try {
                    finalPolygons = difference(finalPolygons, shadowPolygon);
                } catch (e) {
                    console.error("Polygon clipping failed for a segment, falling back to unclipped view for this segment.", e);
                }
            }
        }

        if (finalPolygons.length === 0 || finalPolygons[0].length === 0) return null;

        const finalColor = cam.color || '#16a34a';
        const fillColor = hexToRgba(finalColor, 0.3);

        return (
            <React.Fragment key={`cov-frag-${cam.id}`}>
                {finalPolygons.map((poly, index) => {
                    if (poly.length === 0 || poly[0].length < 3) return null;
                    const pointsStr = poly[0].map(p => `${p[0]},${p[1]}`).join(' ');
                    return (
                        <polygon
                            key={`cov-${cam.id}-${index}`}
                            points={pointsStr}
                            fill={fillColor}
                            stroke={finalColor}
                            strokeWidth="1.5"
                            strokeDasharray="4,2"
                            className="pointer-events-auto"
                            onClick={(e) => {
                                if (mode === 'measuring') return;
                                e.stopPropagation();
                                setState({ selectedCamera: cam, selectedWallId: null });
                            }}
                        />
                    );
                })}
            </React.Fragment>
        );
    };

    return (
        <div className="flex flex-col bg-gray-200 rounded-lg shadow-lg relative" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
            <input type="file" ref={uploadPlanInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
            <input type="file" ref={importDesignInputRef} onChange={onImportFileSelected} accept=".dat" className="hidden" />
            <header className="bg-white p-3 border-b border-gray-300 z-10">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-4 flex-wrap">
                        <button onClick={() => uploadPlanInputRef.current.click()} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-md transition-colors"><Upload className="w-4 h-4" /> {floorPlan ? 'Upload New Plan' : 'Upload Plan'}</button>
                        <button onClick={handleExportToPicture} disabled={!floorPlan} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><ImageIcon className="w-4 h-4" /> Export to Picture</button>
                        <button onClick={() => importDesignInputRef.current.click()} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-md transition-colors"><FileUp className="w-4 h-4" /> Import Design</button>
                        <button onClick={handleExportDesign} disabled={!floorPlan} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Download className="w-4 h-4" /> Export Design</button>
                        <button
                            onClick={() => setIsMagnifierEnabled(prev => !prev)}
                            disabled={!floorPlan}
                            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isMagnifierEnabled ? 'bg-yellow-500 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                                }`}
                        >
                            <Search className="w-4 h-4" />
                            Magnifier
                        </button>
                        {isMagnifierEnabled && (
                            <div className="flex items-center gap-4 ml-2">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Zoom: {magnifierZoom}x</label>
                                    <input
                                        type="range"
                                        min="2"
                                        max="10"
                                        step="1"
                                        value={magnifierZoom}
                                        onChange={(e) => setMagnifierZoom(Number(e.target.value))}
                                        className="w-24 h-2 bg-yellow-200 rounded-lg appearance-none cursor-pointer yellow-slider"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Size: {magnifierSize}px</label>
                                    <input
                                        type="range"
                                        min="100"
                                        max="300"
                                        step="10"
                                        value={magnifierSize}
                                        onChange={(e) => setMagnifierSize(Number(e.target.value))}
                                        className="w-24 h-2 bg-yellow-200 rounded-lg appearance-none cursor-pointer yellow-slider"
                                    />
                                </div>
                            </div>
                        )}
                        {floorPlan && (
                            <div className="flex items-center gap-2 ml-auto">
                                <Search className="w-4 h-4 text-gray-600" />
                                <label htmlFor="zoom-slider" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                    Zoom: {Math.round(canvasZoom * 100)}%
                                </label>
                                <input
                                    id="zoom-slider"
                                    type="range"
                                    min="0.25"
                                    max="3"
                                    step="0.05"
                                    value={canvasZoom}
                                    onChange={(e) => setCanvasZoom(parseFloat(e.target.value))}
                                    className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="w-px h-4 bg-gray-300 mx-2"></div>
                                <ImageIcon className="w-4 h-4 text-gray-600" />
                                <label htmlFor="opacity-slider" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                    Opacity: {Math.round(floorPlanOpacity * 100)}%
                                </label>
                                <input
                                    id="opacity-slider"
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={floorPlanOpacity}
                                    onChange={(e) => setState({ floorPlanOpacity: parseFloat(e.target.value) })}
                                    className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                            </div>
                        )}
                    </div>
                    {floorPlan && (
                        <div className="flex items-center gap-4 flex-wrap pt-2 border-t border-gray-200 mt-2">
                            <button onClick={() => handleSetMode('scaling')} disabled={mode === 'scaling'} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'scaling' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'}`}><Ruler className="w-4 h-4" /> Set Scale</button>
                            <button onClick={handleToggleDrawWallMode} disabled={!scale} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'drawing_wall' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'} disabled:bg-gray-200 disabled:cursor-not-allowed`}><Spline className="w-4 h-4" /> {mode === 'drawing_wall' ? 'Finish Drawing' : 'Draw Wall'}</button>
                            <button onClick={handleToggleWallVisibility} disabled={!scale || walls.length === 0} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 disabled:bg-gray-200 disabled:cursor-not-allowed">
                                {areWallsVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                <span>{areWallsVisible ? 'Hide Walls' : 'Show Walls'}</span>
                            </button>
                            <button onClick={handleToggleMeasureMode} disabled={!scale} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'measuring' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'} disabled:bg-gray-200 disabled:cursor-not-allowed`}><Ruler className="w-4 h-4" /> {mode === 'measuring' ? 'Stop Measure' : 'Measure Distance'}</button>
                            <button onClick={() => handleSetMode('adding_text')} disabled={!scale || mode === 'adding_text'} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'adding_text' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'} disabled:bg-gray-200 disabled:cursor-not-allowed`}><Type className="w-4 h-4" /> Add Text</button>
                            <button onClick={() => handleSetMode('adding_camera')} disabled={!scale || mode === 'adding_camera'} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'adding_camera' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'} disabled:bg-gray-200 disabled:cursor-not-allowed`}><Video className="w-4 h-4" /> Add Camera</button>
                            <div ref={cameraListRef} className="relative">
                                <button onClick={() => setShowCameraList(prev => !prev)} disabled={cameras.length === 0} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 rounded-md transition-colors disabled:bg-gray-200 disabled:cursor-not-allowed">Camera List ({cameras.length}) <ChevronDown className={`w-4 h-4 transition-transform ${showCameraList ? 'rotate-180' : ''}`} /></button>
                                {showCameraList && (
                                    <div className="absolute top-full mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200 z-30">
                                        <ul className="divide-y divide-gray-200 max-h-80 overflow-y-auto">{cameras.map((cam, index) => (<li key={cam.id} className="p-3 flex items-center justify-between text-sm hover:bg-gray-50 gap-2"><div className="flex-1 flex items-center gap-3"><span className="font-bold text-gray-500 w-6 text-center">{index + 1}</span><div className="flex-1"><p className="font-semibold text-gray-800">{cam.model}{cam.properties.lensType ? ` (${getFocalLengthDisplay(cam.properties.lensType)})` : ''}</p><input type="text" placeholder="Custom Name" value={cam.name || ''} onChange={(e) => updateCameraName(cam.id, e.target.value)} className="mt-1 w-full text-xs p-1 border border-gray-300 rounded-md" /></div></div><div className="flex items-center gap-3"><label className="cursor-pointer"><input type="color" value={cam.color || '#16a34a'} onChange={(e) => updateCameraColor(cam.id, e.target.value)} className="w-6 h-6 p-0 border-none bg-transparent appearance-none cursor-pointer" /></label><button onClick={() => toggleCameraVisibility(cam.id)} className="text-gray-500 hover:text-gray-800">{cam.isVisible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}</button></div></li>))}</ul>
                                    </div>
                                )}
                            </div>
                            {scale && <div className="text-sm text-gray-600 ml-auto">Scale: {(scale.ratio * (unitSystem === 'imperial' ? 0.3048 : 1)).toFixed(2)} px/{getUnitLabel(unitSystem)}</div>}
                        </div>
                    )}
                </div>

                {floorPlan && cameras.length > 0 && (
                    <div ref={statsContainerRef} className="mt-3 pt-2 border-t border-gray-200 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-800">
                        <strong className="font-bold">Statics:</strong>
                        {statistics.countBadges.map(badge => (
                            <span key={badge.model} className={`inline-flex items-center px-2 py-0.5 text-white font-semibold rounded-md ${badge.color}`}>
                                {badge.model}: {badge.count}
                            </span>
                        ))}
                        <div className="w-px h-4 bg-gray-300 mx-1"></div>
                        <strong className="font-bold">Total Coverage:</strong>
                        <span className={`inline-flex items-center px-2 py-0.5 text-white font-semibold rounded-md ${statistics.areaBadge.color}`}>
                            {formatArea(statistics.areaBadge.value, unitSystem, 2)}
                        </span>
                        <div className="w-px h-4 bg-gray-300 mx-1"></div>
                        <strong className="font-bold">GPU Recommendation:</strong>
                        <span className={`inline-flex items-center px-2 py-0.5 text-white font-semibold rounded-md ${statistics.gpuBadge.color}`}>
                            {statistics.gpuBadge.text}
                        </span>
                        <div className="relative group flex items-center">
                            <HelpCircle className="w-4 h-4 text-gray-500 cursor-help" />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max hidden group-hover:block bg-black text-white text-xs rounded py-1 px-2 z-20 shadow-lg">
                                GPU is only required for ReID functionality
                            </div>
                        </div>
                    </div>
                )}
            </header>
            <main ref={mainContainerRef} className="flex-grow relative flex items-center justify-center p-4 overflow-auto">
                {floorPlan ? (
                    <div style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${canvasZoom})`, transformOrigin: 'center center', transition: isPanning ? 'none' : 'transform 0.1s ease-out' }}>
                        <div
                            className="relative shadow-md w-full flex items-center justify-center"
                            style={{
                                cursor: mode !== 'idle'
                                    ? 'crosshair'
                                    : (isDragging || isDraggingTextBox)
                                        ? 'grabbing'
                                        : isPanning
                                            ? 'grabbing'
                                            : (floorPlan && !selectedCamera && !selectedWallId && !selectedTextBoxId && !selectedMeasurementId)
                                                ? 'grab'
                                                : 'default'
                            }}
                        >
                            {/* imageContainerRef 放在 inline-block wrapper 上，确保坐标计算与 SVG 一致 */}
                            <div
                                ref={imageContainerRef}
                                className="relative inline-block"
                                onClick={handleImageContainerClick}
                                onDoubleClick={handleImageContainerDoubleClick}
                                onMouseDown={(e) => {
                                    // 在 idle 模式下且没有选中任何东西且编辑器未打开时启动画布拖拽
                                    if (mode === 'idle' && floorPlan && !selectedCamera && !selectedWallId && !selectedTextBoxId && !selectedMeasurementId && !editorCamera && e.button === 0) {
                                        setIsPanning(true);
                                        panStartRef.current = {
                                            x: e.clientX,
                                            y: e.clientY,
                                            initialPanX: panOffset.x,
                                            initialPanY: panOffset.y,
                                        };
                                    }
                                }}
                            >
                                <img src={floorPlan} alt="Floor Plan" draggable="false" onDragStart={(e) => e.preventDefault()} className="block select-none max-w-full h-auto" style={{ opacity: floorPlanOpacity }} />
                                <svg
                                    className="absolute top-0 left-0 w-full h-full pointer-events-none"
                                    viewBox={floorPlanNaturalSize ? `0 0 ${floorPlanNaturalSize.width} ${floorPlanNaturalSize.height}` : undefined}
                                    preserveAspectRatio="none"
                                    style={{ overflow: 'visible' }}
                                >
                                    <g transform="translate(0, 0)">
                                        {scalePoints.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="5" fill="rgba(255, 223, 0, 0.9)" stroke="#000" strokeWidth="1" />)}
                                        {scalePoints.length === 2 && <line x1={scalePoints[0].x} y1={scalePoints[0].y} x2={scalePoints[1].x} y2={scalePoints[1].y} stroke="rgba(255, 223, 0, 0.9)" strokeWidth="3" strokeDasharray="5,5" />}
                                        {measurements.map(m => {
                                            const isSelected = selectedMeasurementId === m.id;
                                            const [p1, p2] = m.points;
                                            const dx = p2.x - p1.x;
                                            const dy = p2.y - p1.y;
                                            const len = Math.sqrt(dx * dx + dy * dy);
                                            if (len === 0) return null;
                                            const nx = dx / len;
                                            const ny = dy / len;
                                            const capLength = 5;
                                            return (
                                                <React.Fragment key={`measure-group-${m.id}`}>
                                                    <g onClick={(e) => { e.stopPropagation(); if (mode !== 'measuring') setState({ selectedMeasurementId: m.id, selectedCamera: null, selectedWallId: null, selectedTextBoxId: null }) }} className="pointer-events-auto cursor-pointer">
                                                        <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#ef4444" strokeWidth="2" />
                                                        <line x1={p1.x - capLength * ny} y1={p1.y + capLength * nx} x2={p1.x + capLength * ny} y2={p1.y - capLength * nx} stroke="#ef4444" strokeWidth="2" />
                                                        <line x1={p2.x - capLength * ny} y1={p2.y + capLength * nx} x2={p2.x + capLength * ny} y2={p2.y - capLength * nx} stroke="#ef4444" strokeWidth="2" />
                                                    </g>
                                                    <g transform={`translate(${(m.points[0].x + m.points[1].x) / 2}, ${(m.points[0].y + m.points[1].y) / 2})`} onClick={(e) => { e.stopPropagation(); if (mode !== 'measuring') setState({ selectedMeasurementId: m.id, selectedCamera: null, selectedWallId: null, selectedTextBoxId: null }) }} className="pointer-events-auto cursor-pointer">
                                                        <rect x="-30" y="-12" width="60" height="18" rx="4" fill="rgba(0,0,0,0.6)" />
                                                        <text textAnchor="middle" alignmentBaseline="middle" fill="white" fontSize="12" fontWeight="bold" style={{ userSelect: 'none' }}>{formatLength(m.distance, unitSystem, 2)}</text>
                                                    </g>
                                                    {isSelected && (
                                                        <g transform={`translate(${((m.points[0].x + m.points[1].x) / 2) + 40}, ${(m.points[0].y + m.points[1].y) / 2})`} onClick={(e) => handleDeleteMeasurement(m.id, e)} className="pointer-events-auto cursor-pointer">
                                                            <circle r="10" fill="rgba(239, 68, 68, 0.9)" />
                                                            <X color="white" size={12} x={-6} y={-6} />
                                                        </g>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}
                                        {measurePoints.map((p, i) => <circle key={`measure-pt-current-${i}`} cx={p.x} cy={p.y} r="5" fill="rgba(239, 68, 68, 0.9)" stroke="#fff" strokeWidth="1.5" />)}
                                        {mode === 'measuring' && measurePoints.length === 1 && (
                                            <line
                                                x1={measurePoints[0].x}
                                                y1={measurePoints[0].y}
                                                x2={snappedMousePosition.x}
                                                y2={snappedMousePosition.y}
                                                stroke="#ef4444"
                                                strokeWidth="2"
                                                strokeDasharray="5,5"
                                            />
                                        )}
                                        {currentWallPoints.length > 0 && <polyline points={currentWallPoints.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="rgba(239, 68, 68, 0.8)" strokeWidth="3" strokeDasharray="5,5" />}
                                        {currentWallPoints.map((p, i) => <circle key={`wall-pt-${i}`} cx={p.x} cy={p.y} r={i === 0 && isSnappingToStart && currentWallPoints.length >= 2 ? 8 : 4} fill="rgba(239, 68, 68, 0.8)" />)}
                                        {areWallsVisible && walls.map(wall => <polyline key={wall.id} points={wall.points.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={wall.id === selectedWallId ? '#f59e0b' : '#374151'} strokeWidth={wall.id === selectedWallId ? 4 : 3} className="pointer-events-auto cursor-pointer" onClick={(e) => { e.stopPropagation(); if (mode !== 'measuring') setState({ selectedWallId: wall.id, selectedCamera: null }); }} />)}
                                        {cameras.map(cam => { const coverageData = cameraCoverages[cam.id]; if (!coverageData) return null; return renderCoveragePolygon(cam, coverageData); })}
                                        {cameras.map((cam, index) => {
                                            if (!cam.isVisible) return null;
                                            const isSelected = selectedCamera?.id === cam.id;
                                            const baseColor = cam.color || '#16a34a';
                                            const innerColor = (cam.isNew || isSelected) ? '#ef4444' : baseColor;
                                            const outerColor = (cam.isNew || isSelected) ? 'rgba(239, 68, 68, 0.5)' : hexToRgba(baseColor, 0.5);
                                            const labelText = `${index + 1}${cam.name ? ` - ${cam.name}` : ''}`;
                                            let iconPosition = { ...cam.position };
                                            return (<React.Fragment key={cam.id}>
                                                <g transform={`translate(${iconPosition.x}, ${iconPosition.y}) rotate(${cam.properties.rotation || 0})`} onMouseDown={(e) => handleCameraMouseDown(e, cam)} onClick={(e) => e.stopPropagation()} className="pointer-events-auto cursor-grab">
                                                    <circle r="16" fill={outerColor} />
                                                    <circle r="12" fill={innerColor} stroke="#fff" strokeWidth="2" />
                                                    <Camera className="text-white" x="-8" y="-8" width="16" height="16" />
                                                </g>
                                                <g transform={`translate(${iconPosition.x + 20}, ${iconPosition.y - 8})`} style={{ pointerEvents: 'none' }}>
                                                    <rect x="0" y="0" width={labelText.length * 7 + 8} height="16" rx="4" fill="rgba(0, 0, 0, 0.5)" />
                                                    <text x="4" y="11" fill="#fff" fontSize="12" fontWeight="bold" style={{ userSelect: 'none' }}>
                                                        {labelText}
                                                    </text>
                                                </g>
                                            </React.Fragment>);
                                        })}
                                        {selectedCamera && editorCamera?.id !== selectedCamera.id && (
                                            <foreignObject x={selectedCamera.position.x + 20} y={selectedCamera.position.y} width="100" height="40" className="pointer-events-auto cursor-pointer">
                                                <div
                                                    xmlns="http://www.w3.org/1999/xhtml"
                                                    data-edit-button-id={selectedCamera.id}
                                                    onClick={(e) => { e.stopPropagation(); handleOpenEditorClick(); }}
                                                >
                                                    <button className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-md transition-colors shadow-lg">
                                                        <Edit className="w-4 h-4" /> Edit
                                                    </button>
                                                </div>
                                            </foreignObject>
                                        )}
                                        {areWallsVisible && selectedWallId && (() => {
                                            const wall = walls.find(w => w.id === selectedWallId);
                                            if (!wall || wall.points.length < 2) return null;
                                            const p1 = wall.points[0];
                                            const p2 = wall.points[1];
                                            const x = (p1.x + p2.x) / 2;
                                            const y = (p1.y + p2.y) / 2;
                                            return (
                                                <g transform={`translate(${x}, ${y})`} onClick={(e) => {
                                                    e.stopPropagation();
                                                    const newWalls = walls.filter(w => w.id !== selectedWallId);
                                                    setState({
                                                        walls: newWalls,
                                                        selectedWallId: null,
                                                        floorPlanOpacity: newWalls.length === 0 ? 1.0 : 0.5
                                                    });
                                                }} className="pointer-events-auto cursor-pointer">
                                                    <circle r="12" fill="rgba(239, 68, 68, 0.9)" stroke="#fff" strokeWidth="1.5" />
                                                    <X color="white" size={16} x={-8} y={-8} />
                                                </g>
                                            );
                                        })()}
                                        {areWallsVisible && selectedWallId && (() => {
                                            const wall = walls.find(w => w.id === selectedWallId);
                                            if (!wall) return null;
                                            return wall.points.map((p, index) => (
                                                <circle
                                                    key={`wall-endpoint-${wall.id}-${index}`}
                                                    cx={p.x}
                                                    cy={p.y}
                                                    r="6"
                                                    fill="#f59e0b"
                                                    stroke="#fff"
                                                    strokeWidth="2"
                                                    className="pointer-events-auto cursor-move"
                                                    onMouseDown={(e) => handleWallPointMouseDown(e, wall.id, index)}
                                                />
                                            ));
                                        })()}
                                        {textBoxes.map(tb => {
                                            const foreignObjectPadding = 15;
                                            if (tb.isEditing) {
                                                return (
                                                    <foreignObject key={tb.id} x={tb.position.x} y={tb.position.y} width="150" height="40" className="pointer-events-auto">
                                                        <input
                                                            type="text" value={tb.text || ''} autoFocus onFocus={e => e.target.select()}
                                                            onChange={e => handleTextBoxTextChange(tb.id, e.target.value)}
                                                            onBlur={() => handleTextBoxBlur(tb.id)}
                                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleTextBoxBlur(tb.id); } }}
                                                            style={{
                                                                padding: '8px 16px', fontSize: `${tb.fontSize}px`, color: tb.theme === 'dark' ? 'white' : 'black',
                                                                background: tb.theme === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)',
                                                                border: '1px solid #06b6d4', borderRadius: '6px', outline: 'none',
                                                            }}
                                                            onClick={e => e.stopPropagation()}
                                                        />
                                                    </foreignObject>
                                                );
                                            }
                                            const isSelected = selectedTextBoxId === tb.id;
                                            const isDark = tb.theme === 'dark';
                                            const bgColor = isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)';
                                            const textColor = isDark ? '#FFFFFF' : '#000000';
                                            const toggleThemeColor = isDark ? 'white' : 'black';
                                            return (
                                                <foreignObject
                                                    key={tb.id}
                                                    x={tb.position.x - foreignObjectPadding}
                                                    y={tb.position.y - foreignObjectPadding}
                                                    width="330" height="130"
                                                    style={{ pointerEvents: 'none' }}
                                                >
                                                    <div
                                                        xmlns="http://www.w3.org/1999/xhtml"
                                                        data-textbox-id={tb.id}
                                                        onMouseDown={e => handleTextBoxMouseDown(e, tb)}
                                                        onDoubleClick={e => handleTextBoxDoubleClick(e, tb.id)}
                                                        onClick={e => { e.stopPropagation(); if (mode !== 'measuring') setState({ selectedTextBoxId: tb.id, selectedCamera: null, selectedWallId: null, selectedMeasurementId: null }) }}
                                                        style={{
                                                            position: 'absolute', top: `${foreignObjectPadding}px`, left: `${foreignObjectPadding}px`,
                                                            display: 'inline-block', cursor: 'grab', background: bgColor, color: textColor,
                                                            padding: '8px 16px', borderRadius: '6px', fontSize: `${tb.fontSize}px`,
                                                            userSelect: 'none', border: isSelected ? '2px solid #06b6d4' : 'none',
                                                            pointerEvents: 'auto',
                                                        }}
                                                    >
                                                        <span>{tb.text || ''}</span>
                                                        {isSelected && (
                                                            <>
                                                                <button onClick={e => handleTextBoxThemeChange(tb.id, e)} style={{
                                                                    position: 'absolute', top: -8, left: -8, width: 16, height: 16,
                                                                    borderRadius: '50%', background: toggleThemeColor, border: '1px solid grey', cursor: 'pointer',
                                                                }} />
                                                                <button onClick={e => handleDeleteTextBox(tb.id, e)} style={{
                                                                    position: 'absolute', top: -8, right: -8, width: 18, height: 18,
                                                                    borderRadius: '50%', background: '#ef4444', color: 'white',
                                                                    border: 'none', cursor: 'pointer', display: 'flex',
                                                                    alignItems: 'center', justifyContent: 'center'
                                                                }}>
                                                                    <X size={12} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </foreignObject>
                                            );
                                        })}
                                    </g>
                                </svg>
                                {isMagnifierEnabled && magnifierPosition.visible && (
                                    <>
                                        <div
                                            style={{
                                                left: `${magnifierPosition.cssX - (magnifierSize / magnifierZoom / 2)}px`,
                                                top: `${magnifierPosition.cssY - (magnifierSize / magnifierZoom / 2)}px`,
                                                width: `${magnifierSize / magnifierZoom}px`,
                                                height: `${magnifierSize / magnifierZoom}px`,
                                                border: '2px solid rgba(255, 0, 0, 0.3)',
                                                position: 'absolute',
                                                pointerEvents: 'none',
                                                zIndex: 50,
                                                borderRadius: '50%',
                                            }}
                                        />
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: `${magnifierPosition.cssX + 30}px`,
                                                top: `${magnifierPosition.cssY + 30}px`,
                                                width: `${magnifierSize}px`,
                                                height: `${magnifierSize}px`,
                                                border: '2px solid black',
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                backgroundColor: 'white',
                                                zIndex: 50,
                                                pointerEvents: 'none',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                            }}
                                        >
                                            <canvas ref={magnifierCanvasRef} width={magnifierSize} height={magnifierSize} style={{ borderRadius: '50%' }} />
                                        </div>
                                    </>
                                )}
                                {editorCamera && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: `${editorPosition.x}px`,
                                            top: `${editorPosition.y}px`,
                                            transform: editorPosition.transform,
                                            maxHeight: editorPosition.maxHeight,
                                            zIndex: 30,
                                            cursor: isDraggingEditor ? 'grabbing' : 'grab',
                                        }}
                                        onMouseDown={handleEditorDragStart}
                                    >
                                        <CameraEditor
                                            camera={editorCamera}
                                            onUpdate={updateCameraProperty}
                                            onDelete={deleteCamera}
                                            onClose={() => setEditorCamera(null)}
                                            onDuplicate={duplicateCamera}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full flex gap-6 min-h-[500px] p-8">
                        {/* 左侧：上传新平面图 */}
                        <div
                            onClick={() => uploadPlanInputRef.current.click()}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`flex-1 flex flex-col items-center justify-center border-4 border-dashed rounded-lg transition-colors cursor-pointer py-12 ${isDraggingOver ? 'border-cyan-500 bg-cyan-50' : 'border-gray-300 bg-gray-100 hover:border-cyan-400'}`}
                        >
                            <div className="text-center pointer-events-none">
                                <UploadCloud className="mx-auto h-16 w-16 text-gray-400" />
                                <p className="mt-4 text-lg font-semibold text-gray-600">Upload New Floor Plan</p>
                                <p className="mt-1 text-sm text-gray-500">Click or drag & drop</p>
                                <p className="mt-1 text-xs text-gray-400">Supported: JPG, PNG, GIF</p>
                            </div>
                        </div>
                        {/* 右侧：导入已有设计 */}
                        <div
                            onClick={() => importDesignInputRef.current.click()}
                            className="flex-1 flex flex-col items-center justify-center border-4 border-dashed rounded-lg transition-colors cursor-pointer border-gray-300 bg-gray-100 hover:border-cyan-400 py-12"
                        >
                            <div className="text-center pointer-events-none">
                                <FileUp className="mx-auto h-16 w-16 text-gray-400" />
                                <p className="mt-4 text-lg font-semibold text-gray-600">Import Existing Design</p>
                                <p className="mt-1 text-sm text-gray-500">Click to browse</p>
                                <p className="mt-1 text-xs text-gray-400">Supported: .dat files</p>
                            </div>
                        </div>
                    </div>
                )}
            </main>
            {showScaleModal && (<div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"><div className="bg-white rounded-lg shadow-xl p-6 w-96"><h3 className="text-lg font-bold mb-4">Set Scale Distance</h3><p className="text-sm text-gray-600 mb-4">Enter the real-world distance for the line you just drew.</p><div className="flex items-center gap-2"><input type="number" value={scaleInput} onChange={(e) => setState({ scaleInput: parseFloat(e.target.value) })} className="block w-full border-gray-300 rounded-md shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm p-2" /><span className="text-lg font-medium">{unitSystem === 'imperial' ? 'feet' : 'millimeters'}</span></div><div className="mt-6 flex justify-end gap-3"><button onClick={() => setState({ showScaleModal: false, scalePoints: [], mode: 'idle' })} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button><button onClick={handleConfirmScale} className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700">Confirm</button></div></div></div>)}
            {showCameraModal && (<div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"><div className="bg-white rounded-lg shadow-xl p-6 w-96"><h3 className="text-lg font-bold mb-4">Select Camera Model</h3><div className="space-y-2">{Object.keys(CAMERA_MODELS).map(key => (<button key={key} onClick={() => handleSelectCameraModel(key)} className="w-full text-left p-3 rounded-md hover:bg-gray-100 transition-colors">{CAMERA_MODELS[key].name}</button>))}</div><div className="mt-6 flex justify-end"><button onClick={() => setState({ showCameraModal: false })} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button></div></div></div>)}
        </div >
    );
};

export default PlanDesigner;
